import { THREAD_CHANNEL_TYPES } from '../contracts/chat-permissions.js';
import type {
  APIChannel,
  APIGatewayGuild,
  APIGatewayReady,
  APIGatewayReadySupplemental,
  APIGuild,
  APIGuildMember,
  APIMessage,
  APIPartialEmoji,
  APIPresence,
  APIReaction,
  APIUser,
  GatewayDispatchPayloads,
} from '../contracts/chat-types.js';
import { MessageType } from '../contracts/chat-types.js';
import {
  removeMessages,
  updateMessage,
  upsertMessage,
} from './message-lists.js';
import {
  type ChatState,
  DMS_SETTINGS_KEY,
  type GuildRecord,
  type MemberRecord,
  type PresenceRecord,
  type Snowflake,
} from './state.js';

/** O Discord considera "digitando" por 10 s depois de cada aviso. */
export const TYPING_DURATION_MS = 10_000;

type Payloads = GatewayDispatchPayloads;

/**
 * Aplica um evento do gateway ao estado (função pura: devolve um estado
 * novo, sem mexer no antigo). É o mesmo papel das stores do cliente do
 * Discord: o READY monta tudo e cada evento mexe só no que mudou.
 */
export function applyDispatch(
  state: ChatState,
  t: string,
  d: unknown,
  now: number = Date.now(),
): ChatState {
  switch (t) {
    case 'READY':
      return fromReady(state, d as Payloads['READY']);
    case 'READY_SUPPLEMENTAL':
      return withPresences(state, d as Payloads['READY_SUPPLEMENTAL']);
    case 'GUILD_CREATE':
      return addGuild(state, d as Payloads['GUILD_CREATE']);
    case 'GUILD_UPDATE':
      return updateGuild(state, d as Payloads['GUILD_UPDATE']);
    case 'GUILD_DELETE':
      return removeGuild(state, (d as Payloads['GUILD_DELETE']).id);
    case 'GUILD_ROLE_CREATE':
    case 'GUILD_ROLE_UPDATE': {
      const { guild_id: guildId, role } = d as Payloads['GUILD_ROLE_CREATE'];
      return mapGuild(state, guildId, (guild) => ({
        ...guild,
        roles: { ...guild.roles, [role.id]: role },
      }));
    }
    case 'GUILD_ROLE_DELETE': {
      const { guild_id: guildId, role_id: roleId } =
        d as Payloads['GUILD_ROLE_DELETE'];
      let next = mapGuild(state, guildId, (guild) => {
        const roles = { ...guild.roles };
        delete roles[roleId];
        return { ...guild, roles };
      });
      const members = next.members[guildId];
      if (members) {
        const updated: Record<Snowflake, MemberRecord> = {};
        for (const [userId, member] of Object.entries(members)) {
          updated[userId] = member.roles.includes(roleId)
            ? { ...member, roles: member.roles.filter((id) => id !== roleId) }
            : member;
        }
        next = { ...next, members: { ...next.members, [guildId]: updated } };
      }
      return next;
    }
    case 'GUILD_MEMBER_ADD':
    case 'GUILD_MEMBER_UPDATE': {
      const member = d as Payloads['GUILD_MEMBER_ADD'];
      return withMember(state, member.guild_id, member);
    }
    case 'GUILD_MEMBER_REMOVE': {
      const { guild_id: guildId, user } = d as Payloads['GUILD_MEMBER_REMOVE'];
      const members = state.members[guildId];
      if (!members?.[user.id]) return state;
      const rest = { ...members };
      delete rest[user.id];
      return { ...state, members: { ...state.members, [guildId]: rest } };
    }
    case 'GUILD_EMOJIS_UPDATE': {
      const { guild_id: guildId, emojis } =
        d as Payloads['GUILD_EMOJIS_UPDATE'];
      return mapGuild(state, guildId, (guild) => ({ ...guild, emojis }));
    }
    case 'GUILD_STICKERS_UPDATE': {
      const { guild_id: guildId, stickers } =
        d as Payloads['GUILD_STICKERS_UPDATE'];
      return mapGuild(state, guildId, (guild) => ({ ...guild, stickers }));
    }
    case 'GUILD_SCHEDULED_EVENT_CREATE':
    case 'GUILD_SCHEDULED_EVENT_UPDATE': {
      const event = d as Payloads['GUILD_SCHEDULED_EVENT_CREATE'];
      const previous = state.scheduledEvents[event.guild_id]?.[event.id];
      // A atualização não traz a contagem de interessados: mantém a conhecida;
      // evento novo começa sem ninguém (os interessados chegam um a um).
      const userCount =
        event.user_count ??
        previous?.user_count ??
        (t === 'GUILD_SCHEDULED_EVENT_CREATE' && !previous ? 0 : undefined);
      return withScheduledEvent(state, {
        ...event,
        ...(userCount !== undefined ? { user_count: userCount } : {}),
      });
    }
    case 'GUILD_SCHEDULED_EVENT_DELETE': {
      const event = d as Payloads['GUILD_SCHEDULED_EVENT_DELETE'];
      const events = { ...(state.scheduledEvents[event.guild_id] ?? {}) };
      delete events[event.id];
      return {
        ...state,
        scheduledEvents: { ...state.scheduledEvents, [event.guild_id]: events },
      };
    }
    case 'GUILD_SCHEDULED_EVENT_USER_ADD':
    case 'GUILD_SCHEDULED_EVENT_USER_REMOVE': {
      const change = d as Payloads['GUILD_SCHEDULED_EVENT_USER_ADD'];
      const event =
        state.scheduledEvents[change.guild_id]?.[
          change.guild_scheduled_event_id
        ];
      if (!event || event.user_count === undefined) return state;
      const delta = t === 'GUILD_SCHEDULED_EVENT_USER_ADD' ? 1 : -1;
      return withScheduledEvent(state, {
        ...event,
        user_count: Math.max(0, event.user_count + delta),
      });
    }
    case 'CHANNEL_CREATE':
    case 'CHANNEL_UPDATE':
    case 'THREAD_CREATE':
    case 'THREAD_UPDATE':
      return withChannel(state, d as APIChannel);
    case 'CHANNEL_DELETE':
      return removeChannel(state, (d as APIChannel).id);
    case 'THREAD_DELETE':
      return removeChannel(state, (d as Payloads['THREAD_DELETE']).id);
    case 'CHANNEL_PINS_UPDATE': {
      const pins = d as Payloads['CHANNEL_PINS_UPDATE'];
      return mapChannel(state, pins.channel_id, (channel) => ({
        ...channel,
        last_pin_timestamp: pins.last_pin_timestamp,
      }));
    }
    case 'CHANNEL_RECIPIENT_ADD':
    case 'CHANNEL_RECIPIENT_REMOVE': {
      const change = d as Payloads['CHANNEL_RECIPIENT_ADD'];
      const next = withUsers(state, [change.user]);
      return mapChannel(next, change.channel_id, (channel) => {
        const others = (channel.recipients ?? []).filter(
          (user) => user.id !== change.user.id,
        );
        return {
          ...channel,
          recipients:
            t === 'CHANNEL_RECIPIENT_ADD' ? [...others, change.user] : others,
        };
      });
    }
    case 'THREAD_MEMBER_UPDATE': {
      const member = d as Payloads['THREAD_MEMBER_UPDATE'];
      if (member.user_id !== state.me?.id) return state;
      const { guild_id: _guild, ...rest } = member;
      return {
        ...state,
        joinedThreads: { ...state.joinedThreads, [member.id]: rest },
      };
    }
    case 'THREAD_MEMBERS_UPDATE': {
      const change = d as Payloads['THREAD_MEMBERS_UPDATE'];
      let next = mapChannel(state, change.id, (channel) => ({
        ...channel,
        member_count: change.member_count,
      }));
      const me = state.me?.id;
      const added = change.added_members?.find(
        (member) => member.user_id === me,
      );
      if (added) {
        next = {
          ...next,
          joinedThreads: { ...next.joinedThreads, [change.id]: added },
        };
      }
      if (me && change.removed_member_ids?.includes(me)) {
        const joined = { ...next.joinedThreads };
        delete joined[change.id];
        next = { ...next, joinedThreads: joined };
      }
      return next;
    }
    case 'MESSAGE_CREATE':
      return messageCreated(state, d as APIMessage);
    case 'MESSAGE_UPDATE': {
      const message = d as APIMessage;
      const list = upsertMessage(state.messages[message.channel_id], message, {
        append: false,
      });
      if (list === state.messages[message.channel_id]) return state;
      return {
        ...state,
        messages: { ...state.messages, [message.channel_id]: list! },
      };
    }
    case 'MESSAGE_DELETE': {
      const { id, channel_id: channelId } = d as Payloads['MESSAGE_DELETE'];
      return messagesDeleted(state, channelId, [id]);
    }
    case 'MESSAGE_DELETE_BULK': {
      const { ids, channel_id: channelId } =
        d as Payloads['MESSAGE_DELETE_BULK'];
      return messagesDeleted(state, channelId, ids);
    }
    case 'MESSAGE_ACK': {
      const ack = d as Payloads['MESSAGE_ACK'];
      const current = state.readStates[ack.channel_id];
      return {
        ...state,
        readStates: {
          ...state.readStates,
          [ack.channel_id]: {
            id: ack.channel_id,
            last_message_id: ack.message_id,
            mention_count: ack.mention_count,
            last_pin_timestamp: current?.last_pin_timestamp ?? null,
          },
        },
      };
    }
    case 'MESSAGE_REACTION_ADD':
    case 'MESSAGE_REACTION_REMOVE': {
      const change = d as Payloads['MESSAGE_REACTION_ADD'];
      const mine = change.user_id === state.me?.id;
      return mapMessage(state, change.channel_id, change.message_id, (m) => ({
        ...m,
        reactions: changeReaction(
          m.reactions,
          change.emoji,
          t === 'MESSAGE_REACTION_ADD' ? 1 : -1,
          change.burst,
          mine,
        ),
      }));
    }
    case 'MESSAGE_REACTION_REMOVE_ALL': {
      const change = d as Payloads['MESSAGE_REACTION_REMOVE_ALL'];
      return mapMessage(state, change.channel_id, change.message_id, (m) => {
        const { reactions: _all, ...rest } = m;
        return rest;
      });
    }
    case 'MESSAGE_REACTION_REMOVE_EMOJI': {
      const change = d as Payloads['MESSAGE_REACTION_REMOVE_EMOJI'];
      const key = emojiKey(change.emoji);
      return mapMessage(state, change.channel_id, change.message_id, (m) => {
        const reactions = (m.reactions ?? []).filter(
          (reaction) => emojiKey(reaction.emoji) !== key,
        );
        const { reactions: _old, ...rest } = m;
        return reactions.length ? { ...rest, reactions } : rest;
      });
    }
    case 'MESSAGE_POLL_VOTE_ADD':
    case 'MESSAGE_POLL_VOTE_REMOVE': {
      const vote = d as Payloads['MESSAGE_POLL_VOTE_ADD'];
      const mine = vote.user_id === state.me?.id;
      const delta = t === 'MESSAGE_POLL_VOTE_ADD' ? 1 : -1;
      return mapMessage(state, vote.channel_id, vote.message_id, (m) => {
        if (!m.poll) return m;
        // Como o Discord, `answer_counts` só lista as respostas com voto:
        // a que recebe o primeiro voto entra aqui.
        const known = m.poll.results?.answer_counts ?? [];
        const counts = known.some((count) => count.id === vote.answer_id)
          ? known
          : [...known, { id: vote.answer_id, count: 0, me_voted: false }];
        return {
          ...m,
          poll: {
            ...m.poll,
            results: {
              is_finalized: m.poll.results?.is_finalized ?? false,
              answer_counts: counts.map((count) =>
                count.id === vote.answer_id
                  ? {
                      ...count,
                      count: Math.max(0, count.count + delta),
                      me_voted: mine ? delta > 0 : count.me_voted,
                    }
                  : count,
              ),
            },
          },
        };
      });
    }
    case 'TYPING_START': {
      const typing = d as Payloads['TYPING_START'];
      if (typing.user_id === state.me?.id) return state;
      let next = state;
      if (typing.member && typing.guild_id) {
        next = withMember(next, typing.guild_id, typing.member);
      }
      const expires = Math.max(
        typing.timestamp * 1000 + TYPING_DURATION_MS,
        now + 1_000,
      );
      return {
        ...next,
        typing: {
          ...next.typing,
          [typing.channel_id]: {
            ...(next.typing[typing.channel_id] ?? {}),
            [typing.user_id]: expires,
          },
        },
      };
    }
    case 'PRESENCE_UPDATE': {
      const presence = d as Payloads['PRESENCE_UPDATE'];
      return {
        ...state,
        presences: {
          ...state.presences,
          [presence.user.id]: presenceRecord(presence),
        },
      };
    }
    case 'USER_SETTINGS_UPDATE':
      return state.userSettings
        ? {
            ...state,
            userSettings: {
              ...state.userSettings,
              ...(d as Payloads['USER_SETTINGS_UPDATE']),
            },
          }
        : state;
    case 'USER_GUILD_SETTINGS_UPDATE': {
      const settings = d as Payloads['USER_GUILD_SETTINGS_UPDATE'];
      return {
        ...state,
        guildSettings: {
          ...state.guildSettings,
          [settings.guild_id ?? DMS_SETTINGS_KEY]: settings,
        },
      };
    }
    case 'USER_UPDATE': {
      const user = d as Payloads['USER_UPDATE'];
      const { bio: _bio, ...rest } = user;
      return {
        ...state,
        me: state.me?.id === user.id ? { ...state.me, ...rest } : state.me,
        users: {
          ...state.users,
          [user.id]: { ...state.users[user.id], ...rest },
        },
      };
    }
    case 'USER_NOTE_UPDATE': {
      const { id, note } = d as Payloads['USER_NOTE_UPDATE'];
      if (!state.notes) return state;
      const notes = { ...state.notes };
      if (note) notes[id] = note;
      else delete notes[id];
      return { ...state, notes };
    }
    default:
      // Banimentos, convites, auditoria e moderação não ficam no estado: as
      // telas de configuração recarregam pela REST e ouvem o evento.
      return state;
  }
}

// ---------------------------------------------------------------------------
// READY e servidores
// ---------------------------------------------------------------------------

function fromReady(state: ChatState, ready: APIGatewayReady): ChatState {
  let next: ChatState = {
    ...state,
    me: ready.user,
    users: { [ready.user.id]: ready.user },
    guilds: {},
    guildIds: [],
    channels: {},
    guildChannelIds: {},
    guildThreadIds: {},
    privateChannelIds: [],
    members: {},
    // Sessão nova: o que estava em memória pode ter perdido eventos.
    messages: {},
    typing: {},
    joinedThreads: {},
    scheduledEvents: {},
    readStates: Object.fromEntries(
      ready.read_state.entries.map((entry) => [entry.id, entry]),
    ),
    userSettings: ready.user_settings,
    guildSettings: Object.fromEntries(
      ready.user_guild_settings.entries.map((entry) => [
        entry.guild_id ?? DMS_SETTINGS_KEY,
        entry,
      ]),
    ),
  };
  for (const guild of ready.guilds) next = addGuild(next, guild);
  for (const channel of ready.private_channels) {
    next = withChannel(next, channel);
  }
  return next;
}

function addGuild(state: ChatState, guild: APIGatewayGuild): ChatState {
  const { channels, threads, members, roles, ...rest }: APIGatewayGuild = guild;
  const record: GuildRecord = {
    ...rest,
    roles: Object.fromEntries(roles.map((role) => [role.id, role])),
  };
  const users = { ...state.users };
  const memberMap: Record<Snowflake, MemberRecord> = {};
  for (const member of members) {
    users[member.user.id] = member.user;
    memberMap[member.user.id] = memberRecord(member);
  }
  const channelMap = { ...state.channels };
  for (const channel of [...channels, ...threads]) {
    channelMap[channel.id] = { ...channel, guild_id: guild.id };
  }
  const joined = { ...state.joinedThreads };
  for (const thread of threads) {
    if (thread.member) joined[thread.id] = thread.member;
  }
  return {
    ...state,
    users,
    guilds: { ...state.guilds, [guild.id]: record },
    guildIds: state.guildIds.includes(guild.id)
      ? state.guildIds
      : [...state.guildIds, guild.id],
    channels: channelMap,
    guildChannelIds: {
      ...state.guildChannelIds,
      [guild.id]: channels.map((channel) => channel.id),
    },
    guildThreadIds: {
      ...state.guildThreadIds,
      [guild.id]: threads.map((thread) => thread.id),
    },
    members: { ...state.members, [guild.id]: memberMap },
    joinedThreads: joined,
  };
}

function updateGuild(state: ChatState, guild: APIGuild): ChatState {
  const current = state.guilds[guild.id];
  if (!current) return state;
  const { roles, ...rest } = guild;
  return {
    ...state,
    guilds: {
      ...state.guilds,
      [guild.id]: {
        ...current,
        ...rest,
        roles: Object.fromEntries(roles.map((role) => [role.id, role])),
      },
    },
  };
}

function removeGuild(state: ChatState, guildId: Snowflake): ChatState {
  if (!state.guilds[guildId]) return state;
  const ids = new Set([
    ...(state.guildChannelIds[guildId] ?? []),
    ...(state.guildThreadIds[guildId] ?? []),
  ]);
  const without = <T>(record: Record<string, T>, keys: Set<string>) => {
    const copy = { ...record };
    for (const key of keys) delete copy[key];
    return copy;
  };
  const guildKey = new Set([guildId]);
  return {
    ...state,
    guilds: without(state.guilds, guildKey),
    guildIds: state.guildIds.filter((id) => id !== guildId),
    channels: without(state.channels, ids),
    guildChannelIds: without(state.guildChannelIds, guildKey),
    guildThreadIds: without(state.guildThreadIds, guildKey),
    members: without(state.members, guildKey),
    messages: without(state.messages, ids),
    pending: without(state.pending, ids),
    typing: without(state.typing, ids),
    joinedThreads: without(state.joinedThreads, ids),
    scheduledEvents: without(state.scheduledEvents, guildKey),
  };
}

function withPresences(
  state: ChatState,
  supplemental: APIGatewayReadySupplemental,
): ChatState {
  const presences = { ...state.presences };
  for (const list of supplemental.merged_presences.guilds) {
    for (const presence of list) {
      const id = presence.user_id ?? presence.user?.id;
      if (id) presences[id] = presenceRecord(presence);
    }
  }
  return { ...state, presences };
}

// ---------------------------------------------------------------------------
// Canais
// ---------------------------------------------------------------------------

function withChannel(state: ChatState, channel: APIChannel): ChatState {
  const previous = state.channels[channel.id];
  const merged: APIChannel = previous ? { ...previous, ...channel } : channel;
  delete merged.newly_created;
  const { member, ...stored } = merged;
  let next: ChatState = {
    ...state,
    channels: { ...state.channels, [channel.id]: stored },
  };
  if (member && member.user_id === state.me?.id) {
    next = {
      ...next,
      joinedThreads: { ...next.joinedThreads, [channel.id]: member },
    };
  }
  const guildId = merged.guild_id;
  if (guildId) {
    const key = THREAD_CHANNEL_TYPES.has(merged.type)
      ? 'guildThreadIds'
      : 'guildChannelIds';
    const ids = next[key][guildId] ?? [];
    if (!ids.includes(channel.id)) {
      next = {
        ...next,
        [key]: { ...next[key], [guildId]: [...ids, channel.id] },
      };
    }
    return next;
  }
  next = withUsers(next, merged.recipients ?? []);
  if (!next.privateChannelIds.includes(channel.id)) {
    next = {
      ...next,
      privateChannelIds: [channel.id, ...next.privateChannelIds],
    };
  }
  return next;
}

function removeChannel(state: ChatState, channelId: Snowflake): ChatState {
  const channel = state.channels[channelId];
  if (!channel) return state;
  const channels = { ...state.channels };
  delete channels[channelId];
  const messages = { ...state.messages };
  delete messages[channelId];
  const pending = { ...state.pending };
  delete pending[channelId];
  const joined = { ...state.joinedThreads };
  delete joined[channelId];
  const guildId = channel.guild_id;
  const drop = (lists: Record<Snowflake, Snowflake[]>) =>
    guildId && lists[guildId]
      ? {
          ...lists,
          [guildId]: lists[guildId].filter((id) => id !== channelId),
        }
      : lists;
  return {
    ...state,
    channels,
    messages,
    pending,
    joinedThreads: joined,
    guildChannelIds: drop(state.guildChannelIds),
    guildThreadIds: drop(state.guildThreadIds),
    privateChannelIds: state.privateChannelIds.filter((id) => id !== channelId),
  };
}

// ---------------------------------------------------------------------------
// Mensagens
// ---------------------------------------------------------------------------

function messageCreated(state: ChatState, message: APIMessage): ChatState {
  const channelId = message.channel_id;
  const me = state.me?.id;
  const mine = message.author.id === me && !message.webhook_id;
  let next = message.webhook_id ? state : withUsers(state, [message.author]);
  if (message.member && message.guild_id && !message.webhook_id) {
    next = withMember(next, message.guild_id, {
      ...message.member,
      user: message.author,
    });
  }

  // A mensagem que eu mandei voltou: sai da lista de pendentes.
  if (mine && message.nonce !== undefined) {
    const pending = next.pending[channelId];
    if (pending?.some((item) => item.nonce === String(message.nonce))) {
      next = {
        ...next,
        pending: {
          ...next.pending,
          [channelId]: pending.filter(
            (item) => item.nonce !== String(message.nonce),
          ),
        },
      };
    }
  }

  const list = upsertMessage(next.messages[channelId], message, {
    append: true,
  });
  if (list !== next.messages[channelId]) {
    next = { ...next, messages: { ...next.messages, [channelId]: list! } };
  }

  const channel = next.channels[channelId];
  if (channel) {
    const isThread = THREAD_CHANNEL_TYPES.has(channel.type);
    // A mensagem de início e a primeira do post (mesmo ID da thread) não contam.
    const counts =
      isThread &&
      message.type !== MessageType.THREAD_STARTER_MESSAGE &&
      message.id !== channelId
        ? {
            message_count: (channel.message_count ?? 0) + 1,
            total_message_sent: (channel.total_message_sent ?? 0) + 1,
          }
        : {};
    next = {
      ...next,
      channels: {
        ...next.channels,
        [channelId]: { ...channel, last_message_id: message.id, ...counts },
      },
    };
    if (!channel.guild_id && next.privateChannelIds[0] !== channelId) {
      // DM com mensagem nova sobe para o topo.
      next = {
        ...next,
        privateChannelIds: [
          channelId,
          ...next.privateChannelIds.filter((id) => id !== channelId),
        ],
      };
    }
  }

  // Leitura: o que eu escrevi está lido; menção a mim conta.
  const read = next.readStates[channelId];
  if (mine) {
    next = {
      ...next,
      readStates: {
        ...next.readStates,
        [channelId]: {
          id: channelId,
          last_message_id: message.id,
          mention_count: 0,
          last_pin_timestamp: read?.last_pin_timestamp ?? null,
        },
      },
    };
  } else if (mentionsMe(next, message)) {
    next = {
      ...next,
      readStates: {
        ...next.readStates,
        [channelId]: {
          id: channelId,
          last_message_id: read?.last_message_id ?? null,
          mention_count: (read?.mention_count ?? 0) + 1,
          last_pin_timestamp: read?.last_pin_timestamp ?? null,
        },
      },
    };
  }

  // Quem mandou parou de digitar.
  if (next.typing[channelId]?.[message.author.id]) {
    const typing = { ...next.typing[channelId] };
    delete typing[message.author.id];
    next = { ...next, typing: { ...next.typing, [channelId]: typing } };
  }
  return next;
}

/**
 * A mensagem conta como menção para mim: em DM e grupo, toda mensagem dos
 * outros (como o Discord); no servidor, menção direta, a um cargo meu ou
 * @everyone/@here.
 */
export function mentionsMe(state: ChatState, message: APIMessage): boolean {
  const me = state.me?.id;
  if (!me || message.author.id === me) return false;
  const channel = state.channels[message.channel_id];
  const guildId = channel?.guild_id ?? message.guild_id;
  if (channel && !guildId) return true;
  if (message.mentions.some((user) => user.id === me)) return true;
  if (message.mention_everyone) return true;
  const roles = guildId ? state.members[guildId]?.[me]?.roles : undefined;
  return !!roles?.some((role) => message.mention_roles.includes(role));
}

function messagesDeleted(
  state: ChatState,
  channelId: Snowflake,
  ids: Snowflake[],
): ChatState {
  const before = state.messages[channelId];
  const list = removeMessages(before, ids);
  const channel = state.channels[channelId];
  let next = state;
  if (list !== before) {
    next = { ...next, messages: { ...next.messages, [channelId]: list! } };
  }
  if (channel && THREAD_CHANNEL_TYPES.has(channel.type)) {
    next = {
      ...next,
      channels: {
        ...next.channels,
        [channelId]: {
          ...channel,
          message_count: Math.max(0, (channel.message_count ?? 0) - ids.length),
        },
      },
    };
  }
  return next;
}

function emojiKey(emoji: APIPartialEmoji): string {
  return emoji.id ?? emoji.name ?? '';
}

/** Soma ou tira uma reação (a própria marca `me`/`me_burst`). */
function changeReaction(
  reactions: APIReaction[] | undefined,
  emoji: APIPartialEmoji,
  delta: 1 | -1,
  burst: boolean,
  mine: boolean,
): APIReaction[] | undefined {
  const list = reactions ?? [];
  const key = emojiKey(emoji);
  const index = list.findIndex((reaction) => emojiKey(reaction.emoji) === key);
  if (index < 0) {
    if (delta < 0) return reactions;
    return [
      ...list,
      {
        emoji,
        count: 1,
        count_details: { burst: burst ? 1 : 0, normal: burst ? 0 : 1 },
        burst_colors: [],
        me_burst: mine && burst,
        burst_me: mine && burst,
        me: mine && !burst,
        burst_count: burst ? 1 : 0,
      },
    ];
  }
  const current = list[index];
  // Evento repetido da própria reação (ex.: já aplicada na tela): não conta duas vezes.
  if (mine && delta > 0 && (burst ? current.me_burst : current.me)) {
    return reactions;
  }
  if (mine && delta < 0 && !(burst ? current.me_burst : current.me)) {
    return reactions;
  }
  const details = {
    burst: Math.max(0, current.count_details.burst + (burst ? delta : 0)),
    normal: Math.max(0, current.count_details.normal + (burst ? 0 : delta)),
  };
  const count = details.burst + details.normal;
  if (count === 0) {
    const rest = list.filter((_, position) => position !== index);
    return rest.length ? rest : undefined;
  }
  const updated: APIReaction = {
    ...current,
    count,
    count_details: details,
    burst_count: details.burst,
    ...(mine && burst
      ? { me_burst: delta > 0, burst_me: delta > 0 }
      : mine
        ? { me: delta > 0 }
        : {}),
  };
  return list.map((reaction, position) =>
    position === index ? updated : reaction,
  );
}

// ---------------------------------------------------------------------------
// Ajudantes
// ---------------------------------------------------------------------------

function mapGuild(
  state: ChatState,
  guildId: Snowflake,
  update: (guild: GuildRecord) => GuildRecord,
): ChatState {
  const guild = state.guilds[guildId];
  if (!guild) return state;
  return { ...state, guilds: { ...state.guilds, [guildId]: update(guild) } };
}

function mapChannel(
  state: ChatState,
  channelId: Snowflake,
  update: (channel: APIChannel) => APIChannel,
): ChatState {
  const channel = state.channels[channelId];
  if (!channel) return state;
  return {
    ...state,
    channels: { ...state.channels, [channelId]: update(channel) },
  };
}

function mapMessage(
  state: ChatState,
  channelId: Snowflake,
  messageId: Snowflake,
  update: (message: APIMessage) => APIMessage,
): ChatState {
  const before = state.messages[channelId];
  const list = updateMessage(before, messageId, update);
  if (list === before) return state;
  return { ...state, messages: { ...state.messages, [channelId]: list! } };
}

function withUsers(state: ChatState, users: APIUser[]): ChatState {
  if (!users.length) return state;
  return {
    ...state,
    users: {
      ...state.users,
      ...Object.fromEntries(users.map((user) => [user.id, user])),
    },
  };
}

function memberRecord(
  member: Omit<APIGuildMember, 'user'> & { user: APIUser },
): MemberRecord {
  const { user, ...rest } = member;
  const { guild_id: _guild, ...fields } = rest as typeof rest & {
    guild_id?: string;
  };
  return { ...fields, user_id: user.id };
}

function withMember(
  state: ChatState,
  guildId: Snowflake,
  member: Omit<APIGuildMember, 'user'> & { user?: APIUser },
): ChatState {
  if (!member.user) return state;
  const record = memberRecord({ ...member, user: member.user });
  return {
    ...withUsers(state, [member.user]),
    members: {
      ...state.members,
      [guildId]: {
        ...(state.members[guildId] ?? {}),
        [record.user_id]: record,
      },
    },
  };
}

function withScheduledEvent(
  state: ChatState,
  event: Payloads['GUILD_SCHEDULED_EVENT_CREATE'],
): ChatState {
  return {
    ...state,
    scheduledEvents: {
      ...state.scheduledEvents,
      [event.guild_id]: {
        ...(state.scheduledEvents[event.guild_id] ?? {}),
        [event.id]: event,
      },
    },
  };
}

function presenceRecord(presence: APIPresence): PresenceRecord {
  return {
    status: presence.status,
    client_status: presence.client_status ?? {},
    activities: presence.activities ?? [],
  };
}
